import { type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Bible Strong's OWN auth shell — deliberately NOT the shared `AuthLayout`
 * (the split brand-panel + glass `AuthCard` shell every clone inherited, which
 * is exactly why they all read as clones of the same template).
 *
 * Where listto de-cloned towards a *cardless* editorial column (form full-bleed
 * on the ambient background, no surface at all), Bible Strong takes the opposite
 * structural choice: a single **bound "reading page"** — one warm, bordered
 * parchment surface where the masthead (open-book glyph + serif wordmark +
 * per-screen eyebrow) and the form live together on the SAME surface, separated
 * by a hairline rule, like the head of a printed page over its body. There is no
 * split panel, no two-column layout, no glass card floating in space: one calm
 * sheet, centered, that reads like opening a book rather than filling a SaaS
 * form. Two products opened side by side do not look like the same shell.
 *
 *   ┌───────────────────────────────┐
 *   │   ▣  Bible Strong   ·eyebrow·  │  ← masthead on the page surface
 *   │   Serif headline.             │
 *   │   Supporting line.            │
 *   │  ───────────────────────────  │  ← hairline rule across the page
 *   │   email   [_____________]     │  ← form on the SAME surface (integrated)
 *   │   pass    [_____________]     │
 *   │   [ Sign in ]                 │
 *   └───────────────────────────────┘
 *
 * The shell stays app-agnostic about the *form*: screens pour their fields in as
 * `children`. The masthead headline IS the page h1 (there is no separate card
 * title), so accessibility keeps exactly one h1 per screen.
 *
 * ── SCROLL CONTRACT (#13 — same fix as the kit, melodimals #4 redux) ────────
 * `AppBackground` is `position: fixed; inset: 0; overflow: hidden` and its slot
 * is `h-full`, so anything rendered INSIDE it gets clipped — a tall form
 * (register, or a short landscape viewport) became unreachable with no scroll.
 * Here `AppBackground` is used purely as a fixed decorative backdrop SIBLING and
 * the content lives in its own scroll container: `min-h-[100dvh]` +
 * `overflow-y-auto` (never `h-dvh`/`overflow-hidden`), with a `min-h-full`
 * inner wrapper (`flex-col justify-center`) so the page centers when it fits and
 * grows-and-scrolls when it does not. Safe-area padding keeps it comfortable on
 * notched devices/PWAs.
 */

import { AppBackground } from "@/components/app-background";

interface AuthPageProps {
  /** Eyebrow chip (e.g. "Welcome back"). Optional. */
  eyebrow?: string;
  /** Page headline — the editorial promise for this screen. Optional for bare loading states. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the headline. The masthead headline IS the page h1 for
   * Bible Strong's auth (there is no separate card title). Pass "h2" only if the
   * screen owns its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** Width of the page. "narrow" for login/forgot/reset/verify, "wide" for register. */
  width?: "narrow" | "wide";
  /** The form (fields + actions), rendered on the page surface below the masthead. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

export function AuthPage(props: AuthPageProps) {
  const { t } = useI18n();

  const Heading = (headingProps: { children: JSX.Element }) =>
    (props.headingLevel ?? "h1") === "h2" ? (
      <h2 class="text-balance font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
        {headingProps.children}
      </h2>
    ) : (
      <h1 class="text-balance font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
        {headingProps.children}
      </h1>
    );

  return (
    <>
      {/* Fixed decorative backdrop — never the scroll parent (see contract above). */}
      <AppBackground />

      <div
        class="fixed inset-0 z-10 min-h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-4 sm:px-6"
        style={{
          "padding-top": "max(1.5rem, env(safe-area-inset-top))",
          "padding-bottom": "max(1.5rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1rem, env(safe-area-inset-left))",
          "padding-right": "max(1rem, env(safe-area-inset-right))",
        }}
      >
        <div
          class="mx-auto flex min-h-full w-full flex-col justify-center py-8"
          classList={{
            "max-w-md": (props.width ?? "narrow") === "narrow",
            "max-w-lg": props.width === "wide",
          }}
        >
          {/* The bound reading page: masthead + hairline + form on ONE surface. */}
          <div class="min-h-0 overflow-hidden rounded-3xl border border-border bg-card/55 shadow-xl shadow-black/5 ring-1 ring-black/5 backdrop-blur-md">
            <header class="flex flex-col gap-4 px-6 pt-9 pb-6 sm:px-9">
              <div class="flex items-center justify-between gap-3">
                <Link
                  to="/"
                  class="group flex items-center gap-2.5 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label={t(msg`Go to home`)}
                >
                  {/* Solid accent chip + open-book glyph: legible on every theme. */}
                  <span class="grid h-11 w-11 place-items-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95">
                    <svg
                      class="h-6 w-6"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      {/* Cross rising over the book */}
                      <path d="M12 2.6v3.6M10.5 4h3" stroke-width="1.7" />
                      {/* Open book */}
                      <path
                        d="M12 8.8C10.4 7.7 8.2 7.3 5.7 7.6c-.5 0-.8.4-.8.9v9c0 .5.4.9.9.8 2.2-.2 4.2.2 5.7 1.2.3.2.7.2 1 0 1.5-1 3.5-1.4 5.7-1.2.5 0 .9-.3.9-.8v-9c0-.5-.3-.9-.8-.9-2.5-.3-4.7.1-6.3 1.2Z"
                        fill="currentColor"
                        fill-opacity="0.16"
                      />
                      {/* Central spine */}
                      <path d="M12 8.8v11" />
                    </svg>
                  </span>
                  <span class="font-serif text-lg font-semibold tracking-tight text-foreground">
                    Bible <span class="font-bold">Strong</span>
                  </span>
                </Link>
                <Show when={props.eyebrow}>
                  <span class="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground shadow-sm ring-1 ring-black/5">
                    {props.eyebrow}
                  </span>
                </Show>
              </div>

              <Show when={props.headline}>
                <div class="space-y-2">
                  <Heading>{props.headline}</Heading>
                  <Show when={props.tagline}>
                    <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                      {props.tagline}
                    </p>
                  </Show>
                </div>
              </Show>
            </header>

            {/* Hairline rule running the full width of the page (no card spine). */}
            <div class="h-px w-full bg-border/70" />

            {/* Form lives on the SAME page surface, below the masthead. */}
            <div class="px-6 py-7 sm:px-9">{props.children}</div>
          </div>

          <Show when={props.footer}>
            <footer class="mt-6 space-y-3 text-center">{props.footer}</footer>
          </Show>
        </div>
      </div>
    </>
  );
}
